import ApiService, {PaginationParams} from "@/service/ApiService.ts";
import {Address, PagedList} from "@/models/commons.ts";

export interface UserListResponse {
    id: string;
    fullName: string;
    firstName: string;
    lastName: string;
    email: string;
    createdAt: Date;
    modifiedAt: Date;
}

export interface CreateUserRequest {
    firstName: string;
    lastName: string;
    email: string;
}

export interface ConsolidatedMailItem {
    mailId: string;
    trackingNumber: string;
}

export interface Task {
    id: string;
    mailboxId: string;
    mailboxNumber: number;
    mailId: string;
    trackingNumber: string;
    type: number;
    createdAt: Date;
    forwardGroupId?: string;
    consolidatedItems?: ConsolidatedMailItem[];
    event: {
        requestedTracking: boolean;
        requestedMostEconomical: boolean;
        requestedByDate?: Date,
        address?: Address
        comment?: string
        costOfShipping?: number
        carrierType?: string
    }
}

export class UserService extends ApiService {

    async getUsers({page, pageSize}: PaginationParams = {page:1, pageSize: 10}): Promise<PagedList<UserListResponse>> {

        const response = await this.get(`/api/users?page=${page}&pageSize=${pageSize}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async getUserById(userId: string): Promise<UserListResponse> {
        const response = await this.get(`/api/users/${userId}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async createUser(user: CreateUserRequest) {
        const response = await this.post('/api/users', user);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async updateUser(userId: string, user: CreateUserRequest) {
        const response = await this.patch(`/api/users/${userId}`, user);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }
    
    async getNotifications(): Promise<{requests:number, tasks:number, expiringIds:number, missingData:number, overDueAccounts:number}> {
        const response = await this.get('/api/users/notifications');

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }
    
    async getTasks() : Promise<Task[]> {
        const response = await this.get('/api/users/tasks');
        
        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }
    
    async completeTask(taskId: string, trackingNumber?: string, costOfShipping?: number, numberOfPages?: number, scannedDocument?: File, carrierType?: string) {
        const formData = new FormData();
        // ts-ignore
        const payload = {
            taskId,
            trackingNumber,
            costOfShipping,
            carrierType,
            numberOfPages
        };

        formData.append('request', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
        if (scannedDocument) formData.append('scannedDocument', scannedDocument);

        const response = await this.postFormData('/api/users/tasks/complete', formData);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async consolidateForwardRequests(taskIds: string[], shippingCost: number, carrierType?: string): Promise<string> {
        const response = await this.post('/api/users/tasks/consolidate-forward', {
            taskIds,
            shippingCost,
            carrierType
        });

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }
}